import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { scrollToId } from "../lib/scroll";
import { useLang } from "../context/LanguageContext";
import Hero from "../components/Hero";
import Marquee from "../components/Marquee";
import ListingCard from "../components/ListingCard";
import StatsStrip from "../components/StatsStrip";
import CreditRepair from "../components/CreditRepair";
import DownPayment from "../components/DownPayment";
import ReviewsWall from "../components/ReviewsWall";
import AgentSpotlight from "../components/AgentSpotlight";
import CTABanner from "../components/CTABanner";

const Home = () => {
  const { t } = useLang();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const { hash } = useLocation();

  useEffect(() => {
    api.get("/listings").then(({ data }) => setListings(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (hash) {
      const tm = setTimeout(() => scrollToId(hash.slice(1)), 150);
      return () => clearTimeout(tm);
    }
  }, [hash]);

  const forSale = listings.filter((l) => l.status === "for_sale");
  const sold = listings.filter((l) => l.status === "sold").slice(0, 3);

  return (
    <>
      <Hero />
      <Marquee />

      <section data-testid="home-listings-section" className="mx-auto max-w-7xl px-4 py-24 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow">{t("home.nowShowing")}</p>
            <h2 className="mt-4 font-serif text-3xl font-bold tracking-tight text-navy sm:text-4xl">
              {t("home.currentListings")}
            </h2>
          </div>
          <Link
            to="/listings"
            data-testid="view-all-listings-link"
            className="group flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-navy hover:text-crimson"
          >
            {t("home.viewAll")}
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>

        {loading ? (
          <div className="mt-12 grid gap-8 md:grid-cols-2">
            {[0, 1].map((i) => <div key={i} className="aspect-[4/3] animate-pulse bg-paper-cream" />)}
          </div>
        ) : (
          <div className="mt-12 grid gap-8 md:grid-cols-2">
            {forSale.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
          </div>
        )}

        {sold.length > 0 && (
          <>
            <p className="mt-20 font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">
              {t("home.recentlySold")}
            </p>
            <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {sold.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
            </div>
          </>
        )}
      </section>

      <StatsStrip />
      <CreditRepair />
      <DownPayment />
      <ReviewsWall />
      <AgentSpotlight />
      <CTABanner />
    </>
  );
};

export default Home;
